import { ReactElement, useContext, useEffect, useState } from 'react'
import { UserContext } from '../../../context/UserContext'
import UserAction from '../../../context/action/UserAction'
import { UpdateLicenseUI } from '../../../model/UpdateLicenseUI'
import DevelopusInputNumber from '../../../../../common/ui/input/DevelopusInputNumber'
import { t } from 'i18next'
import DevelopusDatePicker from '../../../../../common/ui/input/DevelopusDatePicker'
import DevelopusInputTextarea from '../../../../../common/ui/input/DevelopusInputTextarea'
import DevelopusButton from '../../../../../common/ui/button/DevelopusButton'
import DevelopusCancelButton from '../../../../../common/ui/button/DevelopusCancelButton'
import { SaveOutlined } from '@mui/icons-material'
import { dateFromDatePicker } from '../../../../../common/utils/date-utils'
import DevelopusInputText from '../../../../../common/ui/input/DevelopusInputText'

const LicenseEdit = (): ReactElement => {
  const context = useContext(UserContext)
  const action = new UserAction(context)

  const [license, setLicense] = useState<UpdateLicenseUI | undefined>(undefined)

  useEffect(() => {
    const lic = action.getSelectedLicense()

    if (lic !== undefined) {
      setLicense(lic.toUI())
    }
  }, [action.getSelectedLicense()])

  const examFailNumberPlaceholder = t('users.licenses.examFailNumber')
  const examFailNumberLabel = t('users.licenses.examFailNumber')
  const successDateLabel = t('users.licenses.successDate')
  const licenseRenewalDateLabel = t('users.licenses.licenseRenewalDate')
  const descriptionLabel = t('users.licenses.description')
  const licenseProtocolNumberPlaceholder = t(
    'users.licenses.licenseProtocolNumber',
  )
  const licenseProtocolNumberLabel = t('users.licenses.licenseProtocolNumber')

  const examFailNumberHandler = (value: number) => {
    if (license !== undefined) {
      const updatedLicense = {
        ...license,
        examFailNumber: value,
      } satisfies UpdateLicenseUI
      setLicense(updatedLicense)
    }
  }

  const successDateHandler = (value: Date) => {
    if (license !== undefined) {
      const updatedLicense = {
        ...license,
        successDate: dateFromDatePicker(value),
      } satisfies UpdateLicenseUI
      setLicense(updatedLicense)
    }
  }

  const licenseRenewalDateHandler = (value: Date) => {
    if (license !== undefined) {
      const updatedLicense = {
        ...license,
        renewalDate: dateFromDatePicker(value),
      } satisfies UpdateLicenseUI
      setLicense(updatedLicense)
    }
  }

  const descriptionHandler = (value: string) => {
    if (license !== undefined) {
      const updatedLicense = {
        ...license,
        description: value,
      } satisfies UpdateLicenseUI
      setLicense(updatedLicense)
    }
  }

  const licenseProtocolNumberHandler = (value: string) => {
    if (license !== undefined) {
      const updatedLicense = {
        ...license,
        licenseProtocolNumber: value,
      } satisfies UpdateLicenseUI
      setLicense(updatedLicense)
    }
  }

  const cancel = () => {
    action.deselectLicense()
  }

  const updateLicense = () => {
    if (license !== undefined) {
      action.updateSelectedLicense(license)
      action.deselectLicense()
    }
  }

  return (
    <>
      {license !== undefined && (
        <>
          <DevelopusInputNumber
            value={license.examFailNumber}
            onChange={examFailNumberHandler}
            placeholder={examFailNumberPlaceholder}
            label={examFailNumberLabel}
          />

          <DevelopusInputText
            defaultValue={license.licenseProtocolNumber}
            onChange={licenseProtocolNumberHandler}
            placeholder={licenseProtocolNumberPlaceholder}
            label={licenseProtocolNumberLabel}
          />

          <DevelopusDatePicker
            label={successDateLabel}
            value={license.successDate}
            onChange={successDateHandler}
          />

          <DevelopusDatePicker
            label={licenseRenewalDateLabel}
            value={license.renewalDate}
            onChange={licenseRenewalDateHandler}
          />

          <DevelopusInputTextarea
            defaultValue={license.description}
            onChange={descriptionHandler}
            label={descriptionLabel}
          />

          <div style={{ textAlign: 'right' }}>
            <DevelopusCancelButton
              handleClick={cancel}
              text={t('common.cancel')}
            />
            <DevelopusButton
              handleClick={updateLicense}
              text={t('common.update')}
              icon={<SaveOutlined />}
            />
          </div>
        </>
      )}
    </>
  )
}

export default LicenseEdit
